import { getDownloadURL, ref } from "firebase/storage";
import { useContext, useEffect, useRef, useState } from "react";
import { FirebaseContext } from "src/helpers/firebase";
import { ImageCacheContext } from "src/helpers/imageCache";

export const useFirebaseCachedImageUrl = (uri, defaultUri = "", secondDefaultUri = "", r = 1) => {
  const cache = useContext(ImageCacheContext);
  const { storage } = useContext(FirebaseContext);
  const [imageUrl, setImageUrl] = useState<string>(cache.current.get(uri) ?? "");
  const retries = useRef(r);

  const handleDefault = async () => {
    const defaultUrl = cache.current.get(defaultUri);
    if (defaultUrl) {
      setImageUrl(defaultUrl);
    } else {
      try {
        const downloadRef = ref(storage, defaultUri);
        const url = await getDownloadURL(downloadRef);
        // Cache the fallback under ITS OWN key. Caching it under `uri` poisons every
        // future lookup of the primary: a freshly-uploaded photo's `_light` key would
        // permanently point at the original, which the processing pipeline deletes.
        cache.current.set(defaultUri, url);
        setImageUrl(url);
      } catch (error) {
        const notFound = error.code === "storage/object-not-found";
        if (notFound && secondDefaultUri) await handleSecondDefault();

        // Log error to Sentry, but only if it's not a 404 (handled case)
        const found = !notFound;
        if (found) console.error(error);
      }
    }
  };

  const handleSecondDefault = async () => {
    const secondDefaultUrl = cache.current.get(secondDefaultUri);
    if (secondDefaultUrl) {
      setImageUrl(secondDefaultUrl);
    } else {
      const downloadRef = ref(storage, secondDefaultUri);
      const url = await getDownloadURL(downloadRef);
      cache.current.set(secondDefaultUri, url);
      setImageUrl(url);
    }
  };

  const downloadImage = async (uri: string) => {
    try {
      const downloadRef = ref(storage, uri);
      const url = await getDownloadURL(downloadRef);
      cache.current.set(uri, url);
      setImageUrl(url);
    } catch (error) {
      const notFound = error.code === "storage/object-not-found";
      if (notFound && defaultUri) await handleDefault();

      // Keep polling the primary (the fallback stays on screen meanwhile). Backoff grows
      // 3s, 6s, 9s, then 10s flat — image processing routinely takes 15-30s, longer than
      // the old fixed 3s window, which stranded fresh uploads on the fallback.
      if (retries.current > 0) {
        const attempt = r - retries.current + 1;
        retries.current -= 1;
        setTimeout(() => {
          downloadImage(uri);
        }, Math.min(10000, attempt * 3000));
      }

      // Log error to Sentry, but only if it's not a 404 (handled case)
      const found = !notFound;
      if (found) console.error(error);
    }
  };

  useEffect(() => {
    if (cache.current.has(uri)) {
      const imageUrl = cache.current.get(uri);
      setImageUrl(imageUrl);
    } else {
      downloadImage(uri);
    }
  }, []);

  return imageUrl;
};
